import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const METAL_BG =
  "linear-gradient(135deg,#FFFFFF 0%,#E4E6EA 22%,#A6ABB4 55%,#F1F2F4 78%,#898F99 100%)";

export default function ApplicationModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [referral, setReferral] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setName(""); setEmail(""); setPhone(""); setReferral(""); setMessage("");
      setError(""); setSubmitting(false); setDone(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/applications", { name, email, phone: phone || null, referral: referral || null, message });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="application-modal-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ fontFamily: "'Archivo', system-ui, -apple-system, sans-serif" }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div
            className="absolute inset-0 backdrop-blur-md"
            style={{ background: "rgba(0,0,0,0.72)" }}
            onClick={onClose}
          />
          <motion.div
            data-testid="application-modal"
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-lg p-9 rounded-xl text-neutral-200"
            style={{
              background:
                "radial-gradient(ellipse at 50% -10%, #202226 0%, #0e0f12 55%, #050506 100%)",
              border: "1px solid #22252A",
              boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
            }}
            initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 16, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              data-testid="application-modal-close"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 p-1 text-neutral-500 hover:text-neutral-200 transition-colors"
            >
              <X size={18} strokeWidth={1.5} />
            </button>

            {/* Wellnet metallic logo */}
            <div
              aria-hidden
              className="w-36 h-4 mb-6"
              style={{
                background: METAL_BG,
                WebkitMaskImage: "url(/wellnet-logo.png)",
                maskImage: "url(/wellnet-logo.png)",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskPosition: "left center",
                maskPosition: "left center",
              }}
            />

            {done ? (
              <div data-testid="application-success" className="py-2">
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Received</div>
                <h2
                  className="text-2xl uppercase tracking-[0.3em] mb-6 text-neutral-100"
                  style={{ fontWeight: 400 }}
                >
                  Thank you.
                </h2>
                <p className="text-neutral-400 text-base leading-relaxed">
                  Your application has been received. We respond personally within two days.
                </p>
                <button
                  data-testid="application-success-close"
                  onClick={onClose}
                  className="mt-8 uppercase tracking-[0.3em] text-xs font-bold py-3 px-6 rounded-md hover:-translate-y-0.5 transition-transform"
                  style={{ background: METAL_BG, color: "#08090B" }}
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Membership</div>
                <h2
                  className="text-2xl uppercase tracking-[0.3em] mb-2 text-neutral-100"
                  style={{ fontWeight: 400 }}
                >
                  Apply
                </h2>
                <p className="text-neutral-500 text-sm mb-8">
                  A short note. We respond within two days.
                </p>

                <form onSubmit={submit} className="space-y-6">
                  <Field label="Name" required>
                    <input
                      data-testid="apply-name-input"
                      type="text" required minLength={2}
                      value={name} onChange={(e) => setName(e.target.value)}
                      className="auth-line-input"
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      data-testid="apply-email-input"
                      type="email" required
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      className="auth-line-input"
                    />
                  </Field>
                  <Field label="Phone (optional)">
                    <input
                      data-testid="apply-phone-input"
                      type="tel"
                      value={phone} onChange={(e) => setPhone(e.target.value)}
                      className="auth-line-input"
                    />
                  </Field>
                  <Field label="Who introduced you to Wellnet? (optional)">
                    <input
                      data-testid="apply-referral-input"
                      type="text"
                      value={referral} onChange={(e) => setReferral(e.target.value)}
                      placeholder="A name, publication, or other"
                      className="auth-line-input"
                    />
                  </Field>
                  <Field label="What are you looking for?" required>
                    <textarea
                      data-testid="apply-message-input"
                      required minLength={1} rows={3}
                      value={message} onChange={(e) => setMessage(e.target.value)}
                      className="auth-line-input resize-none"
                      style={{ fontSize: 16 }}
                    />
                  </Field>

                  {error && (
                    <div
                      data-testid="apply-error"
                      className="text-xs uppercase tracking-[0.2em] px-3 py-2"
                      style={{ color: "#D9A05B", border: "1px solid #D9A05B" }}
                    >{error}</div>
                  )}

                  <button
                    data-testid="apply-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="w-full uppercase tracking-[0.3em] text-xs font-bold py-4 rounded-md disabled:opacity-60 hover:-translate-y-0.5 transition-transform"
                    style={{ background: METAL_BG, color: "#08090B" }}
                  >
                    {submitting ? "Sending…" : "Submit Application"}
                  </button>
                </form>
              </>
            )}

            <style>{`
              @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=Archivo:wght@400;500;600;700&display=swap');
              .auth-line-input {
                width: 100%;
                background: transparent;
                border: 0;
                border-bottom: 1px solid #31353C;
                color: #F3F4F6;
                padding: 8px 0;
                font-size: 18px;
                color-scheme: dark;
                transition: border-color .15s;
              }
              .auth-line-input:focus { outline: none; border-bottom-color: #C7CAD1; }
              .auth-line-input::placeholder { color: #6F737B; }
            `}</style>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] mb-2 text-neutral-500">
        {label}{required && <span className="text-neutral-600 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
